import React, { useEffect, useState, useCallback } from 'react';
import { api } from './App';
import { Warning, ArrowRight, Close, Clock } from './Icons';

const PRIORITY_TIERS = {
  1:{label:'Not started',color:'#9ca3af',bg:'rgba(156,163,175,0.12)',stale:false,dot:false},
  2:{label:'In progress',color:'#a78bfa',bg:'rgba(167,139,250,0.12)',stale:false,dot:false},
  3:{label:'Needs work',color:'#e05555',bg:'rgba(224,85,85,0.18)',stale:true,dot:true},
  4:{label:'Needs work',color:'#f87171',bg:'rgba(248,113,113,0.09)',stale:false,dot:true},
  5:{label:'Moderate',color:'#d4903a',bg:'rgba(212,144,58,0.16)',stale:true,dot:false},
  6:{label:'Moderate',color:'#fbbf24',bg:'rgba(251,191,36,0.08)',stale:false,dot:false},
  7:{label:'Strong',color:'#4caf7d',bg:'rgba(76,175,125,0.10)',stale:true,dot:false},
  8:{label:'Strong',color:'#86efac',bg:'rgba(134,239,172,0.06)',stale:false,dot:false},
};

function daysAgo(s){if(!s)return 999;for(const f of[s=>{const[d,m,y]=s.split('.');return new Date(y.length===2?`20${y}`:y,m-1,d);},s=>new Date(s)]){try{const d=f(s);if(!isNaN(d))return Math.floor((Date.now()-d)/86400000);}catch{}}return 999;}

function getPriorityTier(t){const c=t.confidence;const st=t.status||'';const r=daysAgo(t.date_updated)<=7;if(c===null&&!st)return 1;if(c===null&&st)return 2;if(c<0.5&&!r)return 3;if(c<0.5&&r)return 4;if(c<0.7&&!r)return 5;if(c<0.7&&r)return 6;if(c>=0.7&&!r)return 7;return 8;}

function TierBadge({tier}){const t=PRIORITY_TIERS[tier];if(!t)return null;return(<span className="tier-badge" style={{color:t.color,background:t.bg,borderColor:t.color+'55'}}>{t.dot&&<span className="tier-dot" style={{background:t.color}}/>}{t.label}{t.stale&&<span className="tier-stale-tag">stale</span>}</span>);}

function confColor(c){if(c===null||c===undefined)return 'var(--muted)';if(c<0.5)return 'var(--red)';if(c<0.7)return 'var(--amber)';return 'var(--green)';}

function ExamCountdown({label,dateKey,dateVal,onSave,accent}){
  const[editing,setEditing]=useState(false);const[draft,setDraft]=useState(dateVal||'');
  useEffect(()=>{setDraft(dateVal||'');},[dateVal]);
  const save=()=>{onSave(dateKey,draft);setEditing(false);};
  if(!dateVal||editing)return(<div className="exam-card" style={{borderColor:accent}}><div className="exam-card-label" style={{color:accent}}>{label}</div><div style={{display:'flex',gap:8,alignItems:'center',marginTop:8}}><input className="exam-input" type="date" value={draft} onChange={e=>setDraft(e.target.value)} onKeyDown={e=>e.key==='Enter'&&save()}/><button className="countdown-edit" onClick={save}>Save</button></div></div>);
  const exam=new Date(dateVal);const daysLeft=Math.ceil((exam-Date.now())/86400000);const urgency=daysLeft<14?'var(--red)':daysLeft<28?'var(--amber)':accent;
  return(<div className="exam-card" style={{borderColor:accent}}><div className="exam-card-label" style={{color:accent}}>{label}</div><div className="exam-card-days" style={{color:urgency}}>{daysLeft}</div><div className="exam-card-sub">days · {Math.floor(daysLeft/7)}w remaining</div><div className="exam-card-date">{exam.toLocaleDateString('en-GB',{weekday:'short',day:'numeric',month:'short',year:'numeric'})}</div><button className="countdown-edit" onClick={()=>setEditing(true)} style={{marginTop:6}}>Edit</button></div>);
}

function CalibrationAlerts({alerts,onTabChange}){
  if(!alerts||alerts.length===0)return null;
  return(
    <div className="calibration-panel">
      <div className="calibration-header">
        <span className="calibration-title"><Warning size={14} style={{marginRight:4,verticalAlign:'middle'}} /> Confidence vs MCQ accuracy: gaps detected</span>
        <span className="calibration-sub">You've rated yourself higher than your MCQ results suggest on these topics</span>
      </div>
      <div className="calibration-list">
        {alerts.map(t=>(
          <div key={t.id} className="calibration-row" onClick={()=>onTabChange(t.module==='FLK1'?'flk1':'flk2')}>
            <div className="calibration-topic">
              <span className="calibration-name">{t.subtopic}</span>
              <span className="calibration-meta">{t.module} · {t.topic}</span>
            </div>
            <div className="calibration-scores">
              <div className="calibration-score"><span className="calibration-score-label">Self</span><span className="calibration-score-val" style={{color:confColor(t.confidence)}}>{Math.round(t.confidence*100)}%</span></div>
              <span className="calibration-arrow"><ArrowRight size={12} /></span>
              <div className="calibration-score"><span className="calibration-score-label">MCQ</span><span className="calibration-score-val" style={{color:confColor(t.mcq_accuracy)}}>{Math.round(t.mcq_accuracy*100)}%</span></div>
              <span className="calibration-gap">−{t.gap}%</span>
            </div>
          </div>
        ))}
      </div>
      <div className="calibration-note">Click any row to review the topic. Consider lowering confidence to reflect actual MCQ performance.</div>
    </div>
  );
}

function PriorityList({topics,moduleColor,onTabChange,module}){
  if(!topics||topics.length===0)return<div className="priority-list"><div className="priority-empty">All topics on track</div></div>;
  return(
    <div className="priority-list" style={{borderLeftColor:moduleColor}}>
      {topics.map(t=>{const tier=getPriorityTier(t);const info=PRIORITY_TIERS[tier];return(
        <div className="priority-row" key={t.id} onClick={()=>onTabChange(module)}>
          <div className="priority-tier-stripe" style={{background:info?.color,opacity:info?.stale?0.7:0.25}}/>
          <div className="priority-text"><span className="priority-sub">{t.subtopic}</span><span className="priority-meta">{t.topic}</span></div>
          <TierBadge tier={tier}/>
          {t.mcq_accuracy!=null&&(<span className="mcq-acc-badge" style={{color:t.mcq_accuracy<0.5?'var(--red)':t.mcq_accuracy>=0.7?'var(--green)':'var(--amber)',background:t.mcq_accuracy<0.5?'rgba(224,85,85,0.1)':t.mcq_accuracy>=0.7?'rgba(76,175,125,0.1)':'rgba(212,144,58,0.1)',borderColor:t.mcq_accuracy<0.5?'rgba(224,85,85,0.3)':t.mcq_accuracy>=0.7?'rgba(76,175,125,0.3)':'rgba(212,144,58,0.3)'}} title="MCQ accuracy">MCQ {Math.round(t.mcq_accuracy*100)}%</span>)}
          {t.sra_accuracy!=null&&(<span className="mcq-acc-badge" style={{color:t.sra_accuracy<0.5?'var(--red)':t.sra_accuracy>=0.7?'var(--green)':'var(--amber)',background:t.sra_accuracy<0.5?'rgba(224,85,85,0.1)':t.sra_accuracy>=0.7?'rgba(76,175,125,0.1)':'rgba(212,144,58,0.1)',borderColor:t.sra_accuracy<0.5?'rgba(224,85,85,0.3)':t.sra_accuracy>=0.7?'rgba(76,175,125,0.3)':'rgba(212,144,58,0.3)'}} title="SRA Questions accuracy">SRA {Math.round(t.sra_accuracy*100)}%</span>)}
          <span className="priority-conf" style={{color:confColor(t.confidence)}}>{t.confidence!==null?Math.round(t.confidence*100)+'%':'—'}</span>
        </div>
      );})}
    </div>
  );
}

function LastTouchedPanel({topics,module,moduleColor,onConfidenceChange}){
  const[updating,setUpdating]=useState(null);const[confValues,setConfValues]=useState({});
  useEffect(()=>{const v={};topics.forEach(t=>{if(t.confidence!==null)v[t.id]=Math.round(t.confidence*100);});setConfValues(v);},[topics]);
  const handleConfChange=async(topic,newConf)=>{
    const confNum=newConf/100;setUpdating(topic.id);
    await api(`/api/topics/${topic.id}`,{method:'PATCH',body:JSON.stringify({confidence:confNum,status:topic.status||'',date_updated:new Date().toLocaleDateString('en-GB',{day:'2-digit',month:'2-digit',year:'numeric'}).replace(/\//g,'.')})});
    if(topic.confidence!==null&&confNum<topic.confidence-0.1){await api('/api/alerts/create',{method:'POST',body:JSON.stringify({message:`${topic.subtopic} confidence dropped to ${newConf}%. May need a priority planner slot.`,topic_id:topic.id})});}
    setUpdating(null);onConfidenceChange&&onConfidenceChange(topic.id,confNum);
  };
  if(!topics||topics.length===0)return null;
  return(
    <div className="last-touched-panel">
      <div className="last-touched-title"><span style={{color:moduleColor}}>{module}</span>: how are you feeling now?<span className="last-touched-hint">Adjust if your confidence has changed since last revising</span></div>
      {topics.map(t=>{
        const staleness=daysAgo(t.date_updated);const staleText=staleness===999?'never reviewed':staleness===0?'today':staleness===1?'yesterday':`${staleness}d ago`;const isStale=staleness>7;const tier=getPriorityTier(t);const currentConf=confValues[t.id]??(t.confidence!==null?Math.round(t.confidence*100):null);
        return(<div key={t.id} className="touched-row"><div className="touched-left"><div className="touched-name">{t.subtopic}</div><div className="touched-meta"><span className="touched-when" style={{color:isStale?'var(--red)':'var(--muted)'}}>{isStale&&<Warning size={11} style={{marginRight:2,verticalAlign:'middle'}} />}{isStale&&' '}{staleText}</span><TierBadge tier={tier}/></div></div><div className="touched-right">{currentConf!==null?(<><span className="touched-conf" style={{color:confColor(currentConf/100)}}>{currentConf}%</span><input type="range" min="0" max="100" step="5" value={currentConf} className="touched-slider" onChange={e=>setConfValues(prev=>({...prev,[t.id]:parseInt(e.target.value)}))} onMouseUp={e=>handleConfChange(t,parseInt(e.target.value))} onTouchEnd={e=>handleConfChange(t,parseInt(e.currentTarget.value))}/>{updating===t.id&&<span className="touched-saving">saving…</span>}</>):(<span style={{fontSize:11,color:'var(--hint)'}}>no score yet</span>)}</div></div>);
      })}
    </div>
  );
}

const MCQ_MILESTONES=[500,1000,1500,2000,2500,3000,3700];
const MCQ_MESSAGES={
  0:'Start logging MCQs as you practice. Every session sharpens your topic-level accuracy.',
  500:'Great start. You\'re building a performance baseline. Keep going to identify your weak areas.',
  1000:'Over 1,000 questions. You\'re seeing patterns. Candidates who maintain this pace tend to enter the exam with much stronger calibration.',
  1500:'Excellent momentum. Active retrieval at this volume starts to create durable long-term memory.',
  2000:'2,000 MCQs is a meaningful milestone. Consistent, high-volume practice is one of the strongest predictors of exam readiness.',
  2500:'You\'re in the top tier of SQE preparation. Keep your focus on weak topics and stay consistent.',
  3000:'Outstanding commitment. You\'ve completed enough practice to be genuinely well-prepared. Now refine your weak spots.',
};

function MCQMilestone({mcqTotal,mcqTarget}){
  const next=MCQ_MILESTONES.find(m=>m>mcqTotal)||3700;
  const prev=MCQ_MILESTONES.slice().reverse().find(m=>m<=mcqTotal)||0;
  const pct=prev===next?100:Math.round(((mcqTotal-prev)/(next-prev))*100);
  const msg=MCQ_MESSAGES[prev]||MCQ_MESSAGES[3000];
  const target=parseInt(mcqTarget)||40;
  return(
    <div className="mcq-milestone">
      <div className="mcq-milestone-header">
        <div>
          <span className="mcq-milestone-count">{mcqTotal.toLocaleString()}</span>
          <span className="mcq-milestone-label"> MCQs logged</span>
          {mcqTotal<3700&&<span className="mcq-milestone-next"> · next milestone: {next.toLocaleString()}</span>}
        </div>
        <span className="mcq-milestone-target">Daily target: {target}</span>
      </div>
      {mcqTotal<3700&&(
        <div className="mcq-milestone-bar-bg">
          <div className="mcq-milestone-bar-fill" style={{width:pct+'%'}}/>
        </div>
      )}
      <p className="mcq-milestone-msg">{msg}</p>
    </div>
  );
}


const REVISION_PRINCIPLES = [
  {
    title: 'Retrieval beats re-reading',
    text: 'Writing down everything you remember before checking your notes builds far stronger memories than reading the material again. The struggle is the learning.',
  },
  {
    title: 'Stale topics decay faster than you think',
    text: 'Topics you last revised more than 7 days ago have already faded significantly. Prioritise stale topics over recently reviewed ones, even if they feel comfortable.',
  },
  {
    title: 'Interleave, don\'t block',
    text: 'Mixing different topics in a single session — rather than spending an hour on one subject — feels harder but produces better long-term retention.',
  },
  {
    title: 'Calibrate your confidence honestly',
    text: 'If your MCQ accuracy is consistently lower than your self-rating on a topic, your confidence score is inflated. Adjust it down to reflect your actual performance under test conditions.',
  },
  {
    title: 'Space your review at increasing intervals',
    text: 'Reviewing a topic at 1 day, then 3 days, then 7 days creates far stronger memories than reviewing it three times in one week. Let your brain almost forget before you review.',
  },
  {
    title: 'Ask why, not just what',
    text: 'Elaborative interrogation — asking yourself why a rule exists and how it connects to other rules — builds richer understanding that holds up under novel exam questions.',
  },
  {
    title: 'Weak topics deserve more repetitions',
    text: 'It takes more repetitions to reach the same retention level for difficult material. Don\'t spend equal time on all topics — weight your effort toward your weakest areas.',
  },
];

function RevisionPrinciple() {
  const idx = new Date().getDay(); // 0–6
  const p = REVISION_PRINCIPLES[idx % REVISION_PRINCIPLES.length];
  return (
    <div className="science-box" style={{marginTop:16}}>
      <div className="science-title">Revision principle</div>
      <p style={{fontWeight:600,marginBottom:4,fontSize:13}}>{p.title}</p>
      <p style={{marginTop:0}}>{p.text}</p>
    </div>
  );
}

export default function Dashboard({onTabChange}){
  const[stats,setStats]=useState(null);const[settings,setSettings]=useState({});const[checkins,setCheckins]=useState([]);const[dismissedCheckin,setDismissedCheckin]=useState(false);

  useEffect(()=>{api('/api/stats').then(setStats);api('/api/settings').then(setSettings);api('/api/checkin-suggestions').then(setCheckins);},[]);

  const saveSetting=async(key,value)=>{setSettings(prev=>({...prev,[key]:value}));await api('/api/settings',{method:'POST',body:JSON.stringify({[key]:value})});};

  const handleConfidenceChange=useCallback((topicId,newConf)=>{setStats(prev=>{if(!prev)return prev;const upd=arr=>arr.map(t=>t.id===topicId?{...t,confidence:newConf}:t);return{...prev,flk1_touched:upd(prev.flk1_touched||[]),flk2_touched:upd(prev.flk2_touched||[]),flk1_priority:upd(prev.flk1_priority||[]),flk2_priority:upd(prev.flk2_priority||[])};});},[]);

  if(!stats)return<div className="loading">Loading…</div>;
  const pctStarted=Math.round((stats.started/stats.total)*100);const mcqPct=Math.min(100,Math.round((stats.mcq_total/3700)*100));const show=key=>settings[key]!=='0';

  return(
    <div className="dashboard">
      <div className="exam-dates-row">
        <ExamCountdown label="FLK1 exam" dateKey="flk1_exam_date" dateVal={settings.flk1_exam_date} onSave={saveSetting} accent="var(--flk1)"/>
        <ExamCountdown label="FLK2 exam" dateKey="flk2_exam_date" dateVal={settings.flk2_exam_date} onSave={saveSetting} accent="var(--flk2)"/>
      </div>

      <div className="metric-row">
        <div className="metric"><span className="metric-val">{stats.started}<span className="metric-denom">/{stats.total}</span></span><span className="metric-label">Topics tracked</span><div className="mini-bar-bg"><div className="mini-bar-fill" style={{width:pctStarted+'%',background:'var(--accent)'}}/></div></div>
        <div className="metric"><span className="metric-val" style={{color:'var(--red)'}}>{stats.weak}</span><span className="metric-label">Need work <span className="metric-hint">(&lt;50%)</span></span>{stats.started > 0 && <span className="metric-context">of {stats.started} rated</span>}</div>
        <div className="metric"><span className="metric-val" style={{color:'var(--amber)'}}>{stats.moderate||0}</span><span className="metric-label">Moderate <span className="metric-hint">(50-69%)</span></span></div>
        <div className="metric"><span className="metric-val" style={{color:'var(--green)'}}>{stats.strong}</span><span className="metric-label">Strong <span className="metric-hint">(70%+)</span></span></div>
        <div className="metric"><span className="metric-val">{stats.mcq_total.toLocaleString()}</span><span className="metric-label">MCQs done <span className="metric-hint">/ 3,700</span></span><div className="mini-bar-bg"><div className="mini-bar-fill" style={{width:mcqPct+'%',background:'var(--accent)'}}/></div></div>
      </div>

      {show('dash_show_mcq')&&<MCQMilestone mcqTotal={stats.mcq_total} mcqTarget={settings.mcq_daily_target}/>}

      {show('dash_show_calibration')&&stats.calibration_alerts?.length>0&&<CalibrationAlerts alerts={stats.calibration_alerts} onTabChange={onTabChange}/>}

      {show('dash_show_legend')&&(
        <div className="priority-legend">
          <span className="priority-legend-label">Priority:</span>
          {[{color:'#e05555',label:'Needs work (stale)',stale:true},{color:'#f87171',label:'Needs work (recent)',stale:false},{color:'#d4903a',label:'Moderate (stale)',stale:true},{color:'#fbbf24',label:'Moderate (recent)',stale:false},{color:'#4caf7d',label:'Strong (stale)',stale:true},{color:'#9ca3af',label:'Not started',stale:false}].map(item=>(
            <span key={item.label} className="priority-legend-item"><span className="priority-legend-swatch" style={{background:item.color,opacity:item.stale?1:0.5}}/><span style={{color:'var(--muted)',fontSize:10}}>{item.label}</span></span>
          ))}
          <span style={{fontSize:10,color:'var(--hint)',marginLeft:4}}>Stale = 7+ days</span>
        </div>
      )}

      {show('dash_show_priority')&&(
        <div className="dash-cols">
          <div className="dash-col"><div className="section-title" style={{color:'var(--flk1)'}}>FLK1 focus topics</div><PriorityList topics={stats.flk1_priority} moduleColor="var(--flk1)" onTabChange={onTabChange} module="flk1"/></div>
          <div className="dash-col"><div className="section-title" style={{color:'var(--flk2)'}}>FLK2 focus topics</div><PriorityList topics={stats.flk2_priority} moduleColor="var(--flk2)" onTabChange={onTabChange} module="flk2"/></div>
        </div>
      )}

      {show('dash_show_checkin')&&checkins.length>0&&!dismissedCheckin&&(
        <div className="checkin-banner" style={{marginTop:16}}>
          <div className="checkin-header"><span className="checkin-title"><Clock size={14} /> Time for a quick recall check</span><button className="checkin-dismiss" onClick={()=>setDismissedCheckin(true)}><Close size={12} /></button></div>
          <p className="checkin-desc">These topics haven't been recalled recently. A 3-minute session now is worth more than re-reading.</p>
          <div className="checkin-topics">{checkins.slice(0,4).map(t=>(<button key={t.id} className="checkin-topic-btn" onClick={()=>onTabChange('recall')}><span className="checkin-topic-name">{t.subtopic}</span><span className="checkin-topic-meta">{t.last_recall?`Last recalled ${t.days_since_recall}d ago`:'Never recalled'}{' · '}{t.confidence!==null?Math.round(t.confidence*100)+'%':'unscored'}</span></button>))}</div>
          <button className="checkin-go-btn" onClick={()=>onTabChange('recall')}>Start recall practice <ArrowRight size={14} /></button>
        </div>
      )}

      {show('dash_show_touched')&&(stats.flk1_touched?.length>0||stats.flk2_touched?.length>0)&&(
        <div className="dash-cols" style={{marginTop:16}}>
          <LastTouchedPanel topics={stats.flk1_touched||[]} module="FLK1" moduleColor="var(--flk1)" onConfidenceChange={handleConfidenceChange}/>
          <LastTouchedPanel topics={stats.flk2_touched||[]} module="FLK2" moduleColor="var(--flk2)" onConfidenceChange={handleConfidenceChange}/>
        </div>
      )}

      {show('dash_show_principle')&&(
        <RevisionPrinciple />
      )}
    </div>
  );
}
